import Alpine from 'alpinejs';

export default function registerCalendarSettingsIntegrationsStore() {
  const existing = Alpine.store('calendarSettingsIntegrationsStore');
  if (existing) {
    return existing;
  }

  const store = Alpine.store('calendarSettingsIntegrationsStore', {
    calendarId: null,
    userPermission: '',
    stripeAccountStatus: null,
    stripe: {
      isExpanded: false,
      isStatusLoaded: false,
    },

    // InvoiceXpress state
    invoicexpress: {
      isStatusLoaded: false,
      isEnabled: false,
      isExpanded: false,
      apiKey: '',
      apiKeyMasked: '',
      showApiKey: false,
      accountName: '',
      connectionStatus: 'unknown',
      connectionError: '',
      isTestingConnection: false,
      isSaving: false,
      lastConnectionTest: null,
      // Tax mapping
      invoicexpressTaxes: [],
      hightideTaxes: [],
      taxMappings: [],
      unmappedTaxesInUse: [],
      isLoadingTaxes: false,
      showCreateTaxModal: false,
      newTaxName: '',
      newTaxValue: '',
      isCreatingTax: false,
      // Auto-invoice
      autoInvoiceEnabled: false,
      autoInvoiceEnabledAt: null,
      isRunningAutoInvoice: false,
      autoInvoiceLastRun: null,
      // Invoice sequence
      sequences: [],
      isLoadingSequences: false,
      selectedSequenceId: '',
      selectedSequenceName: '',
    },

    // Hostkit state
    hostkit: {
      isVisible: false,
      isStatusLoaded: false,
      isEnabled: false,
      isExpanded: false,
      apiKey: '',
      apiKeyMasked: '',
      showApiKey: false,
      connectionStatus: 'unknown',
      connectionError: '',
      isTestingConnection: false,
      isConnecting: false,
      isLoadingConnections: false,
      connectionRows: [],
      isLoadingProperties: false,
      connections: [],
      selectedConnectionId: '',
      isSelectingProperty: false,
      isCreatingProperty: false,
      isInitialSyncing: false,
      properties: [],
      selectedPropertyId: '',
      selectedPropertyName: '',
      mappingMode: 'all_accommodations',
      accommodations: [],
      selectedAccommodationIds: [],
      isLoadingAccommodations: false,
      isSavingMapping: false,
      unmappedAccommodationsCount: 0,
      createProperty: {
        propertyName: '',
        address: '',
        zip: '',
        city: '',
      },
      initialSyncRangeMode: 'future_only',
      lookbackMonths: 3,
      initialSyncTaskId: '',
    },

    async init() {
      this.initializeSettingsContext();
      await this.fetchStripeAccountStatus();
      await this.loadInvoiceXpressSettings();
      if (this.hostkit.isVisible) {
        await this.loadHostkitStatus();
      } else {
        this.hostkit.isStatusLoaded = true;
      }
    },

    getCsrfToken() {
      const tokenInput = document.querySelector('[name=csrfmiddlewaretoken]');
      if (tokenInput && tokenInput.value) {
        return tokenInput.value;
      }
      const tokenMatch = document.cookie.match(/(?:^|; )csrftoken=([^;]+)/);
      return tokenMatch ? decodeURIComponent(tokenMatch[1]) : '';
    },

    initializeSettingsContext() {
      const container = document.getElementById('calendarSettingsContainer');
      if (!container) {
        return;
      }
      this.calendarId = container.dataset.calendarId || null;
      this.userPermission = container.dataset.userPermission || '';
      this.hostkit.isVisible =
        (container.dataset.showHostkitIntegration || 'false') === 'true';
    },

    async fetchStripeAccountStatus() {
      try {
        if (!this.calendarId) {
          this.stripeAccountStatus = {
            connected: false,
            charges_enabled: false,
            payouts_enabled: false,
            requirements: [],
          };
          return;
        }
        const response = await fetch(
          `/calendar/fetch-stripe-account-status/?calendar_id=${this.calendarId}`,
        );
        const data = await response.json();
        if (response.ok) {
          this.stripeAccountStatus = data;
          return;
        }
      } catch (error) {
        console.error('Error fetching Stripe account status:', error);
      } finally {
        this.stripe.isStatusLoaded = true;
      }
      this.stripeAccountStatus = {
        connected: false,
        charges_enabled: false,
        payouts_enabled: false,
        requirements: [],
      };
    },

    async loadInvoiceXpressSettings() {
      try {
        const response = await fetch('/invoicexpress/settings/');
        if (response.ok) {
          const data = await response.json();
          this.invoicexpress.isEnabled = data.is_enabled;
          this.invoicexpress.accountName = data.account_name || '';
          this.invoicexpress.connectionStatus =
            data.connection_status || 'unknown';
          this.invoicexpress.connectionError =
            data.connection_error_message || '';
          this.invoicexpress.lastConnectionTest = data.last_connection_test;
          this.invoicexpress.autoInvoiceEnabled =
            data.auto_invoice_enabled || false;
          this.invoicexpress.autoInvoiceEnabledAt =
            data.auto_invoice_enabled_at || null;
          this.invoicexpress.selectedSequenceId =
            data.invoice_sequence_id || '';
          this.invoicexpress.selectedSequenceName =
            data.invoice_sequence_name || '';
          if (data.has_api_key) {
            this.invoicexpress.apiKeyMasked = '••••••••••••••••';
          }
          // Load tax mappings and sequences if enabled
          if (
            this.invoicexpress.isEnabled &&
            this.invoicexpress.connectionStatus === 'connected'
          ) {
            await this.loadTaxData();
            await this.loadSequences();
          }
        }
      } catch (error) {
        console.error('Error loading InvoiceXpress settings:', error);
      } finally {
        this.invoicexpress.isStatusLoaded = true;
      }
    },

    async saveInvoiceXpressSettings() {
      this.invoicexpress.isSaving = true;
      try {
        const csrfToken = document.querySelector(
          '[name=csrfmiddlewaretoken]',
        ).value;
        const payload = {
          is_enabled: this.invoicexpress.isEnabled,
          account_name: this.invoicexpress.accountName,
          auto_invoice_enabled: this.invoicexpress.autoInvoiceEnabled,
        };
        // Only send API key if it's not masked
        if (
          this.invoicexpress.apiKey &&
          !this.invoicexpress.apiKey.startsWith('•')
        ) {
          payload.api_key = this.invoicexpress.apiKey;
        }
        const response = await fetch('/invoicexpress/settings/save/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
          body: JSON.stringify(payload),
        });
        const data = await response.json();
        if (data.success) {
          Alpine.store('toastStore').addToast(
            'InvoiceXpress settings saved successfully',
            'success',
          );
          if (data.has_api_key) {
            this.invoicexpress.apiKeyMasked = '••••••••••••••••';
            this.invoicexpress.apiKey = '';
          }
        } else {
          Alpine.store('toastStore').addToast(
            data.error || 'Error saving settings',
            'danger',
          );
        }
      } catch (error) {
        console.error('Error saving InvoiceXpress settings:', error);
        Alpine.store('toastStore').addToast('Error saving settings', 'danger');
      } finally {
        this.invoicexpress.isSaving = false;
      }
    },

    async testConnection() {
      this.invoicexpress.isTestingConnection = true;
      this.invoicexpress.connectionStatus = 'testing';
      try {
        const csrfToken = document.querySelector(
          '[name=csrfmiddlewaretoken]',
        ).value;
        const response = await fetch(
          '/invoicexpress/settings/test-connection/',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': csrfToken,
            },
          },
        );
        const data = await response.json();
        if (data.success) {
          this.invoicexpress.connectionStatus = 'connected';
          this.invoicexpress.connectionError = '';
          Alpine.store('toastStore').addToast(
            'Connection successful!',
            'success',
          );
          // Load tax data and sequences after successful connection
          await this.loadTaxData();
          await this.loadSequences();
        } else {
          this.invoicexpress.connectionStatus = 'failed';
          this.invoicexpress.connectionError =
            data.message || 'Connection failed';
          Alpine.store('toastStore').addToast(
            data.message || 'Connection failed',
            'danger',
          );
        }
      } catch (error) {
        console.error('Error testing connection:', error);
        this.invoicexpress.connectionStatus = 'failed';
        this.invoicexpress.connectionError = 'Network error';
        Alpine.store('toastStore').addToast(
          'Error testing connection',
          'danger',
        );
      } finally {
        this.invoicexpress.isTestingConnection = false;
      }
    },

    async loadTaxData() {
      this.invoicexpress.isLoadingTaxes = true;
      try {
        // Load InvoiceXpress taxes, Hightide taxes, mappings, and unmapped taxes in parallel
        const [ixTaxesRes, htTaxesRes, mappingsRes, unmappedRes] =
          await Promise.all([
            fetch('/invoicexpress/taxes/'),
            fetch('/invoicexpress/taxes/hightide/'),
            fetch('/invoicexpress/tax-mappings/'),
            fetch('/invoicexpress/tax-mappings/unmapped/'),
          ]);

        if (ixTaxesRes.ok) {
          const data = await ixTaxesRes.json();
          this.invoicexpress.invoicexpressTaxes = data.taxes || [];
        }
        if (htTaxesRes.ok) {
          const data = await htTaxesRes.json();
          this.invoicexpress.hightideTaxes = data.taxes || [];
        }
        if (mappingsRes.ok) {
          const data = await mappingsRes.json();
          this.invoicexpress.taxMappings = data.mappings || [];
        }
        if (unmappedRes.ok) {
          const data = await unmappedRes.json();
          this.invoicexpress.unmappedTaxesInUse = data.unmapped_taxes || [];
        }
      } catch (error) {
        console.error('Error loading tax data:', error);
      } finally {
        this.invoicexpress.isLoadingTaxes = false;
      }
    },

    async loadSequences() {
      this.invoicexpress.isLoadingSequences = true;
      try {
        const response = await fetch('/invoicexpress/sequences/');
        if (response.ok) {
          const data = await response.json();
          this.invoicexpress.sequences = data.sequences || [];
        }
      } catch (error) {
        console.error('Error loading sequences:', error);
      } finally {
        this.invoicexpress.isLoadingSequences = false;
      }
    },

    async saveSequence(sequenceId) {
      const sequence = this.invoicexpress.sequences.find(
        (s) => String(s.id) === String(sequenceId),
      );
      const sequenceName = sequence ? sequence.serie : '';

      this.invoicexpress.selectedSequenceId = sequenceId || '';
      this.invoicexpress.selectedSequenceName = sequenceName;

      try {
        const csrfToken = document.querySelector(
          '[name=csrfmiddlewaretoken]',
        ).value;
        const response = await fetch('/invoicexpress/settings/save/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
          body: JSON.stringify({
            invoice_sequence_id: sequenceId || '',
            invoice_sequence_name: sequenceName,
          }),
        });
        const data = await response.json();
        if (data.success) {
          if (sequenceId) {
            Alpine.store('toastStore').addToast(
              'Invoice sequence saved: ' + sequenceName,
              'success',
            );
          } else {
            Alpine.store('toastStore').addToast(
              'Invoice sequence reset to default',
              'success',
            );
          }
        } else {
          Alpine.store('toastStore').addToast(
            data.error || 'Error saving sequence',
            'danger',
          );
        }
      } catch (error) {
        console.error('Error saving sequence:', error);
        Alpine.store('toastStore').addToast('Error saving sequence', 'danger');
      }
    },

    getMappedTaxId(hightideTaxId) {
      const mapping = this.invoicexpress.taxMappings.find(
        (m) => m.hightide_tax_id === hightideTaxId,
      );
      return mapping ? mapping.invoicexpress_tax_id : '';
    },

    async saveTaxMapping(hightideTaxId, invoicexpressTaxId) {
      if (!invoicexpressTaxId) return;

      const ixTax = this.invoicexpress.invoicexpressTaxes.find(
        (t) => String(t.id) === String(invoicexpressTaxId),
      );
      if (!ixTax) return;

      try {
        const csrfToken = document.querySelector(
          '[name=csrfmiddlewaretoken]',
        ).value;
        const response = await fetch('/invoicexpress/tax-mappings/save/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
          body: JSON.stringify({
            hightide_tax_id: hightideTaxId,
            invoicexpress_tax_id: invoicexpressTaxId,
            invoicexpress_tax_name: ixTax.name,
            invoicexpress_tax_value: ixTax.value,
          }),
        });
        const data = await response.json();
        if (data.success) {
          Alpine.store('toastStore').addToast('Tax mapping saved', 'success');
          await this.loadTaxData(); // Refresh mappings
        } else {
          Alpine.store('toastStore').addToast(
            data.error || 'Error saving mapping',
            'danger',
          );
        }
      } catch (error) {
        console.error('Error saving tax mapping:', error);
        Alpine.store('toastStore').addToast('Error saving mapping', 'danger');
      }
    },

    async createInvoiceXpressTax() {
      if (!this.invoicexpress.newTaxName || !this.invoicexpress.newTaxValue) {
        Alpine.store('toastStore').addToast(
          'Please enter tax name and value',
          'warning',
        );
        return;
      }

      this.invoicexpress.isCreatingTax = true;
      try {
        const csrfToken = document.querySelector(
          '[name=csrfmiddlewaretoken]',
        ).value;
        const response = await fetch('/invoicexpress/taxes/create/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
          body: JSON.stringify({
            name: this.invoicexpress.newTaxName,
            value: parseFloat(this.invoicexpress.newTaxValue),
          }),
        });
        const data = await response.json();
        if (data.success) {
          Alpine.store('toastStore').addToast(
            'Tax created in InvoiceXpress',
            'success',
          );
          this.invoicexpress.showCreateTaxModal = false;
          this.invoicexpress.newTaxName = '';
          this.invoicexpress.newTaxValue = '';
          await this.loadTaxData(); // Refresh taxes
        } else {
          Alpine.store('toastStore').addToast(
            data.error || 'Error creating tax',
            'danger',
          );
        }
      } catch (error) {
        console.error('Error creating tax:', error);
        Alpine.store('toastStore').addToast('Error creating tax', 'danger');
      } finally {
        this.invoicexpress.isCreatingTax = false;
      }
    },

    getConnectionStatusBadge() {
      if (!this.invoicexpress.isEnabled) {
        return { class: 'bg-secondary', text: 'Not Enabled' };
      }
      const statusMap = {
        connected: { class: 'bg-success', text: 'Connected' },
        failed: { class: 'bg-danger', text: 'Failed' },
        invalid_key: { class: 'bg-danger', text: 'Invalid API Key' },
        testing: { class: 'bg-warning', text: 'Testing...' },
        unknown: { class: 'bg-secondary', text: 'Not Tested' },
      };
      return (
        statusMap[this.invoicexpress.connectionStatus] || statusMap['unknown']
      );
    },

    getHostkitStatusBadge() {
      if (!this.hostkit.isEnabled) {
        return { class: 'bg-secondary', text: 'Not Enabled' };
      }
      const statusMap = {
        connected: { class: 'bg-success', text: 'Connected' },
        failed: { class: 'bg-danger', text: 'Failed' },
        invalid_key: { class: 'bg-danger', text: 'Invalid API Key' },
        testing: { class: 'bg-warning', text: 'Testing...' },
        unknown: { class: 'bg-secondary', text: 'Not Tested' },
      };
      return statusMap[this.hostkit.connectionStatus] || statusMap['unknown'];
    },

    buildHostkitConnectionRow(connection) {
      const selectedPropertyId = this.normalizePropertyId(
        connection.hostkit_property_id,
      );
      const selectedPropertyName = connection.hostkit_property_name || '';
      const isEnabled = !!connection.is_enabled;
      return {
        id: connection.id,
        isNew: false,
        isEditing: false,
        isEnabled,
        apiKey: '',
        apiKeyMasked: '••••••••••••••••',
        showApiKey: false,
        isTesting: false,
        isConnecting: false,
        isSavingProperty: false,
        isLoadingProperties: false,
        connectionStatus: isEnabled
          ? connection.connection_status || 'unknown'
          : 'disconnected',
        connectionError: connection.connection_error_message || '',
        properties: selectedPropertyId
          ? [
              {
                id: selectedPropertyId,
                property_name:
                  selectedPropertyName || `Property ${selectedPropertyId}`,
              },
            ]
          : [],
        hasLoadedProperties: false,
        selectedPropertyId,
        selectedPropertyName,
        mappingMode: connection.mapping_mode || 'all_accommodations',
        mappedAccommodationsCount: connection.mapped_accommodations_count || 0,
        isActive: !!connection.is_active,
        accommodations: [],
        selectedAccommodationIds: [],
        hasLoadedAccommodations: false,
        isLoadingAccommodations: false,
        isSavingMapping: false,
        isInitialSyncing: false,
        initialSyncTaskId: '',
        initialSyncRangeMode: 'future_only',
        lookbackMonths: 3,
      };
    },

    addHostkitConnectionRow() {
      this.hostkit.connectionRows.push({
        id: '',
        isNew: true,
        isEditing: true,
        apiKey: '',
        apiKeyMasked: '',
        showApiKey: false,
        isTesting: false,
        isConnecting: false,
        isSavingProperty: false,
        isLoadingProperties: false,
        connectionStatus: 'unknown',
        connectionError: '',
        properties: [],
        hasLoadedProperties: false,
        selectedPropertyId: '',
        selectedPropertyName: '',
        mappingMode: 'all_accommodations',
        mappedAccommodationsCount: 0,
        isActive: false,
        accommodations: [],
        selectedAccommodationIds: [],
        hasLoadedAccommodations: false,
        isLoadingAccommodations: false,
        isSavingMapping: false,
        isInitialSyncing: false,
        initialSyncTaskId: '',
        initialSyncRangeMode: 'future_only',
        lookbackMonths: 3,
      });
    },

    async connectHostkitRowFromApiInput(row) {
      if (!row) return;
      const typedApiKey = (row.apiKey || '').toString().trim();
      if (!typedApiKey || row.isConnecting) return;

      const connected = await this.connectHostkitRow(row, {
        showToast: false,
      });
      if (!connected) return;

      const activeRow = row.id
        ? this.hostkit.connectionRows.find(
            (candidate) => String(candidate.id) === String(row.id),
          ) || row
        : row;
      if (!activeRow?.id) return;

      await this.loadHostkitPropertiesForRow(activeRow, { force: true });
    },

    getHostkitRowAccommodationSummary(row) {
      if (!row?.id) return 'Not configured';
      if (row.mappingMode === 'all_accommodations') return 'All accommodations';
      const selected = new Set(row.selectedAccommodationIds || []);
      const selectedNames = (row.accommodations || [])
        .filter((accommodation) => selected.has(accommodation.id))
        .map((accommodation) => accommodation.name)
        .filter(Boolean);
      if (!selectedNames.length) return 'No accommodations mapped';
      if (selectedNames.length <= 2) return selectedNames.join(', ');
      return `${selectedNames.slice(0, 2).join(', ')} +${
        selectedNames.length - 2
      } more`;
    },

    async editHostkitRow(row) {
      if (!row) return;
      row.isEditing = true;
      if (row.id) {
        await Promise.all([
          this.loadHostkitPropertiesForRow(row),
          this.loadHostkitAccommodationsForRow(row),
        ]);
      }
    },

    cancelHostkitRowEdit(row, index = null) {
      if (!row) return;
      if (row.isNew && !row.id && index !== null) {
        this.hostkit.connectionRows.splice(index, 1);
        return;
      }
      row.apiKey = '';
      row.showApiKey = false;
      row.isEditing = false;
    },

    normalizeHostkitProperties(rawProperties) {
      if (!Array.isArray(rawProperties)) return [];
      return rawProperties
        .map((property) => {
          const rawId = property?.id ?? property?.apid ?? '';
          const normalizedId = String(rawId || '').trim();
          if (!normalizedId) return null;
          return {
            ...property,
            id: normalizedId,
            property_name:
              property?.property_name ||
              property?.name ||
              `Property ${normalizedId}`,
          };
        })
        .filter(Boolean);
    },

    normalizePropertyId(value) {
      return String(value || '').trim();
    },

    normalizePropertyName(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');
    },

    reconcileRowSelectedProperty(row) {
      if (!row) return;
      const properties = row.properties || [];
      const selectedId = this.normalizePropertyId(row.selectedPropertyId);
      const selectedNameNorm = this.normalizePropertyName(
        row.selectedPropertyName,
      );

      const byId = selectedId
        ? properties.find(
            (property) => this.normalizePropertyId(property.id) === selectedId,
          )
        : null;
      const byName = selectedNameNorm
        ? properties.find(
            (property) =>
              this.normalizePropertyName(property.property_name) ===
              selectedNameNorm,
          )
        : null;

      // If name and id disagree, prefer the name match (user-visible "current").
      if (
        byName &&
        (!byId ||
          this.normalizePropertyName(byId.property_name) !== selectedNameNorm)
      ) {
        row.selectedPropertyId = this.normalizePropertyId(byName.id);
        row.selectedPropertyName =
          byName.property_name || row.selectedPropertyName;
        return;
      }

      if (byId) {
        row.selectedPropertyId = selectedId;
        row.selectedPropertyName =
          byId.property_name || row.selectedPropertyName;
        return;
      }

      if (byName) {
        row.selectedPropertyId = this.normalizePropertyId(byName.id);
        row.selectedPropertyName =
          byName.property_name || row.selectedPropertyName;
      }
    },

    ensureSelectedPropertyOption(row) {
      if (!row) return;
      const selectedId = this.normalizePropertyId(row.selectedPropertyId);
      if (!selectedId) return;
      const hasSelectedOption = (row.properties || []).some(
        (property) => this.normalizePropertyId(property.id) === selectedId,
      );
      if (hasSelectedOption) return;
      row.properties = [
        {
          id: selectedId,
          property_name: row.selectedPropertyName || `Property ${selectedId}`,
        },
        ...(row.properties || []),
      ];
    },

    syncRowSelectedPropertyIdFromName(row) {
      if (!row) return;
      const selectedId = this.normalizePropertyId(row.selectedPropertyId);
      if (selectedId) return;
      const selectedName = (row.selectedPropertyName || '').toString().trim();
      if (!selectedName) return;
      const matched = (row.properties || []).find((property) => {
        const propertyName = (property?.property_name || '').toString().trim();
        return propertyName.toLowerCase() === selectedName.toLowerCase();
      });
      if (matched?.id) {
        row.selectedPropertyId = this.normalizePropertyId(matched.id);
      }
    },

    rowHasSelectedPropertyOption(row) {
      if (!row?.selectedPropertyId) return false;
      const selectedId = this.normalizePropertyId(row.selectedPropertyId);
      return (row.properties || []).some(
        (property) => this.normalizePropertyId(property.id) === selectedId,
      );
    },

    async loadHostkitPropertiesForRow(row, options = {}) {
      const { force = false } = options;
      if (!this.calendarId || !row?.id) {
        row.properties = [];
        row.hasLoadedProperties = false;
        this.ensureSelectedPropertyOption(row);
        return;
      }
      if (!force && row.hasLoadedProperties) {
        this.reconcileRowSelectedProperty(row);
        this.syncRowSelectedPropertyIdFromName(row);
        this.ensureSelectedPropertyOption(row);
        return;
      }
      row.isLoadingProperties = true;
      try {
        const response = await fetch(
          `/hostkit/api/properties/?calendar_id=${this.calendarId}&connection_id=${row.id}`,
        );
        const data = await response.json();
        if (response.ok && data.success) {
          row.properties = this.normalizeHostkitProperties(data.properties);
          row.hasLoadedProperties = true;
        } else {
          row.properties = [];
          row.hasLoadedProperties = false;
        }
        this.reconcileRowSelectedProperty(row);
        this.syncRowSelectedPropertyIdFromName(row);
        this.ensureSelectedPropertyOption(row);
      } catch (error) {
        console.error('Error loading Hostkit properties for row:', error);
        row.properties = [];
        row.hasLoadedProperties = false;
        this.reconcileRowSelectedProperty(row);
        this.syncRowSelectedPropertyIdFromName(row);
        this.ensureSelectedPropertyOption(row);
      } finally {
        row.isLoadingProperties = false;
      }
    },

    async loadHostkitAccommodationsForRow(row, options = {}) {
      const force = !!options.force;
      if (!this.calendarId || !row?.id) {
        row.accommodations = [];
        row.selectedAccommodationIds = [];
        row.hasLoadedAccommodations = false;
        return;
      }
      if (!force && row.hasLoadedAccommodations) {
        return;
      }
      row.isLoadingAccommodations = true;
      try {
        const response = await fetch(
          `/hostkit/api/accommodations/?calendar_id=${this.calendarId}&connection_id=${row.id}`,
        );
        const data = await response.json();
        if (response.ok && data.success) {
          row.accommodations = Array.isArray(data.accommodations)
            ? data.accommodations
            : [];
          row.selectedAccommodationIds = row.accommodations
            .filter((acc) => acc.is_mapped_to_this)
            .map((acc) => acc.id);
          row.hasLoadedAccommodations = true;
          row.mappedAccommodationsCount = row.selectedAccommodationIds.length;
        } else {
          row.accommodations = [];
          row.selectedAccommodationIds = [];
          row.hasLoadedAccommodations = false;
        }
      } catch (error) {
        console.error('Error loading Hostkit accommodations for row:', error);
        row.accommodations = [];
        row.selectedAccommodationIds = [];
        row.hasLoadedAccommodations = false;
      } finally {
        row.isLoadingAccommodations = false;
      }
    },

    async testHostkitConnectionRow(row) {
      if (!this.calendarId || !row) return;
      const typedApiKey = (row.apiKey || '').toString().trim();
      if (!typedApiKey && !row.id) return;
      row.isTesting = true;
      row.connectionStatus = 'testing';
      row.connectionError = '';
      try {
        const response = await fetch('/hostkit/api/test-connection/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          body: JSON.stringify({
            calendar_id: this.calendarId,
            connection_id: row.id || null,
            api_key: typedApiKey || '',
            hostkit_property_id: row.selectedPropertyId || '',
          }),
        });
        const data = await response.json();
        if (response.ok && data.success) {
          row.connectionStatus = 'connected';
          row.connectionError = '';
          Alpine.store('toastStore').addToast(
            'Hostkit connection successful',
            'success',
          );
        } else {
          row.connectionStatus = 'failed';
          row.connectionError = data.error || 'Connection failed';
          Alpine.store('toastStore').addToast(
            data.error || 'Connection failed',
            'danger',
          );
        }
      } catch (error) {
        console.error('Error testing Hostkit row connection:', error);
        row.connectionStatus = 'failed';
        row.connectionError = 'Network error';
        Alpine.store('toastStore').addToast(
          'Error testing Hostkit connection',
          'danger',
        );
      } finally {
        row.isTesting = false;
      }
    },

    async connectHostkitRow(row, options = {}) {
      const { showToast = true } = options;
      if (!this.calendarId || !row) return;
      const typedApiKey = (row.apiKey || '').toString().trim();
      if (!typedApiKey && !row.id) return;
      row.isConnecting = true;
      row.connectionError = '';
      try {
        const response = await fetch('/hostkit/api/connect/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          body: JSON.stringify({
            calendar_id: this.calendarId,
            connection_id: row.id || null,
            api_key: typedApiKey || '',
          }),
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          row.connectionStatus = 'failed';
          row.connectionError = data.error || 'Hostkit connection failed';
          if (showToast) {
            Alpine.store('toastStore').addToast(
              data.error || 'Hostkit connection failed',
              'danger',
            );
          }
          return false;
        }

        this.hostkit.isEnabled = true;
        row.id = data.connection_id || row.id;
        row.isNew = !row.id;
        row.connectionStatus = 'connected';
        row.connectionError = '';
        row.apiKey = '';
        row.apiKeyMasked = '••••••••••••••••';
        const connectedProperties = this.normalizeHostkitProperties(
          data.properties,
        );
        row.properties = connectedProperties;
        row.hasLoadedProperties = connectedProperties.length > 0;
        row.hasLoadedAccommodations = false;
        await this.loadHostkitStatus();
        const refreshedRow = this.hostkit.connectionRows.find(
          (candidate) => candidate.id === row.id,
        );
        if (refreshedRow) {
          refreshedRow.properties = connectedProperties;
          refreshedRow.hasLoadedProperties = connectedProperties.length > 0;
          this.reconcileRowSelectedProperty(refreshedRow);
          this.syncRowSelectedPropertyIdFromName(refreshedRow);
          this.ensureSelectedPropertyOption(refreshedRow);
        }
        if (refreshedRow?.isEditing) {
          await this.loadHostkitAccommodationsForRow(refreshedRow, {
            force: true,
          });
        }
        if (showToast) {
          Alpine.store('toastStore').addToast(
            'Hostkit property connection saved',
            'success',
          );
        }
        return true;
      } catch (error) {
        console.error('Error connecting Hostkit row:', error);
        row.connectionError = 'Network error';
        if (showToast) {
          Alpine.store('toastStore').addToast(
            'Error connecting Hostkit',
            'danger',
          );
        }
        return false;
      } finally {
        row.isConnecting = false;
      }
    },

    async saveHostkitRow(row) {
      if (!row) return;
      if (!row.isEnabled) {
        await this.connectHostkitRow(row);
        if (row.id) {
          row.isNew = false;
          row.isEditing = true;
        }
        return;
      }
      if (!row.id) {
        await this.connectHostkitRow(row);
        if (row.id) {
          row.isNew = false;
          row.isEditing = true;
        }
        return;
      }
      if ((row.apiKey || '').trim()) {
        await this.connectHostkitRow(row);
        return;
      }
      const propertySaved = await this.saveHostkitRowProperty(row, {
        showToast: false,
      });
      if (!propertySaved) return;
      Alpine.store('toastStore').addToast('Hostkit property saved', 'success');
      row.isEditing = true;
    },

    async saveHostkitRowProperty(row, options = {}) {
      const { showToast = true } = options;
      if (!this.calendarId || !row?.id || !row.selectedPropertyId) return;
      row.isSavingProperty = true;
      try {
        const selected = row.properties.find(
          (prop) => String(prop.id) === String(row.selectedPropertyId),
        );
        const response = await fetch('/hostkit/api/select-property/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          body: JSON.stringify({
            calendar_id: this.calendarId,
            connection_id: row.id,
            hostkit_property_id: String(row.selectedPropertyId),
            hostkit_property_name:
              selected?.property_name || row.selectedPropertyName || '',
          }),
        });
        const data = await response.json();
        if (response.ok && data.success) {
          row.selectedPropertyId = this.normalizePropertyId(
            row.selectedPropertyId,
          );
          row.selectedPropertyName =
            selected?.property_name || row.selectedPropertyName;
          this.ensureSelectedPropertyOption(row);
          row.hasLoadedAccommodations = false;
          await this.loadHostkitAccommodationsForRow(row, { force: true });
          if (showToast) {
            Alpine.store('toastStore').addToast(
              'Hostkit property selected',
              'success',
            );
          }
          return true;
        } else {
          if (showToast) {
            Alpine.store('toastStore').addToast(
              data.error || 'Failed to select property',
              'danger',
            );
          }
          return false;
        }
      } catch (error) {
        console.error('Error saving Hostkit row property:', error);
        if (showToast) {
          Alpine.store('toastStore').addToast(
            'Error selecting Hostkit property',
            'danger',
          );
        }
        return false;
      } finally {
        row.isSavingProperty = false;
      }
    },

    async saveHostkitAccommodationMappingRow(row, options = {}) {
      const { showToast = true } = options;
      if (!this.calendarId || !row?.id) return;
      row.isSavingMapping = true;
      try {
        const response = await fetch('/hostkit/api/accommodation-mapping/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          body: JSON.stringify({
            calendar_id: this.calendarId,
            connection_id: row.id,
            mapping_mode: row.mappingMode,
            accommodation_ids:
              row.mappingMode === 'specific_accommodations'
                ? row.selectedAccommodationIds
                : [],
          }),
        });
        const data = await response.json();
        if (response.ok && data.success) {
          if (showToast) {
            Alpine.store('toastStore').addToast(
              'Accommodation mapping saved',
              'success',
            );
          }
          row.hasLoadedAccommodations = false;
          await this.loadHostkitAccommodationsForRow(row, { force: true });
          return true;
        } else {
          if (showToast) {
            Alpine.store('toastStore').addToast(
              data.error || 'Failed to save accommodation mapping',
              'danger',
            );
          }
          return false;
        }
      } catch (error) {
        console.error('Error saving Hostkit row accommodation mapping:', error);
        if (showToast) {
          Alpine.store('toastStore').addToast(
            'Error saving accommodation mapping',
            'danger',
          );
        }
        return false;
      } finally {
        row.isSavingMapping = false;
      }
    },

    async runHostkitInitialSyncForRow(row) {
      if (!this.calendarId || !row?.id) return;
      row.isInitialSyncing = true;
      try {
        const response = await fetch('/hostkit/api/initial-sync/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          body: JSON.stringify({
            calendar_id: this.calendarId,
            connection_id: row.id,
            range_mode: row.initialSyncRangeMode || 'future_only',
            lookback_months:
              row.initialSyncRangeMode === 'future_plus_months'
                ? Number(row.lookbackMonths || 0)
                : 0,
          }),
        });
        const data = await response.json();
        if (response.ok && data.success) {
          row.initialSyncTaskId = data.task_id || '';
          Alpine.store('toastStore').addToast(
            'Hostkit initial sync started',
            'success',
          );
        } else {
          Alpine.store('toastStore').addToast(
            data.error || 'Failed to start initial sync',
            'danger',
          );
        }
      } catch (error) {
        console.error('Error starting Hostkit row initial sync:', error);
        Alpine.store('toastStore').addToast(
          'Error starting Hostkit initial sync',
          'danger',
        );
      } finally {
        row.isInitialSyncing = false;
      }
    },

    async disconnectHostkitRow(row) {
      if (!this.calendarId || !row?.id) return;
      try {
        const response = await fetch('/hostkit/api/disconnect/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          body: JSON.stringify({
            calendar_id: this.calendarId,
            connection_id: row.id,
          }),
        });
        const data = await response.json();
        if (response.ok && data.success) {
          await this.loadHostkitStatus();
          Alpine.store('toastStore').addToast(
            'Hostkit property disconnected',
            'success',
          );
        } else {
          Alpine.store('toastStore').addToast(
            data.error || 'Failed to disconnect Hostkit',
            'danger',
          );
        }
      } catch (error) {
        console.error('Error disconnecting Hostkit row:', error);
        Alpine.store('toastStore').addToast(
          'Error disconnecting Hostkit',
          'danger',
        );
      }
    },

    async deleteHostkitRow(row) {
      if (!this.calendarId || !row?.id) return;
      const confirmed = window.confirm(
        'Delete this Hostkit property row? This will remove the saved API key/property mapping from Hightide.',
      );
      if (!confirmed) return;
      try {
        const response = await fetch('/hostkit/api/delete-connection/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          body: JSON.stringify({
            calendar_id: this.calendarId,
            connection_id: row.id,
          }),
        });
        const data = await response.json();
        if (response.ok && data.success) {
          await this.loadHostkitStatus();
          Alpine.store('toastStore').addToast(
            'Hostkit property row deleted',
            'success',
          );
        } else {
          Alpine.store('toastStore').addToast(
            data.error || 'Failed to delete Hostkit property row',
            'danger',
          );
        }
      } catch (error) {
        console.error('Error deleting Hostkit row:', error);
        Alpine.store('toastStore').addToast(
          'Error deleting Hostkit property row',
          'danger',
        );
      }
    },

    async loadHostkitStatus() {
      if (!this.calendarId) return;
      this.hostkit.isLoadingConnections = true;
      try {
        const response = await fetch(
          `/hostkit/api/status/?calendar_id=${this.calendarId}`,
        );
        if (!response.ok) return;
        const data = await response.json();
        this.hostkit.isEnabled = !!data.is_enabled;
        this.hostkit.connectionStatus = data.connection_status || 'unknown';
        this.hostkit.connectionError = data.connection_error_message || '';
        this.hostkit.connections = Array.isArray(data.connections)
          ? data.connections
          : [];
        const draftRows = this.hostkit.connectionRows.filter(
          (row) => row.isNew,
        );
        const existingRowsById = new Map(
          this.hostkit.connectionRows
            .filter((row) => row.id)
            .map((row) => [String(row.id), row]),
        );
        this.hostkit.connectionRows = this.hostkit.connections.map(
          (connection) => {
            const nextRow = this.buildHostkitConnectionRow(connection);
            const existing = existingRowsById.get(String(connection.id));
            if (!existing) return nextRow;
            return {
              ...nextRow,
              isEditing: existing.isEditing,
              properties:
                existing.isEditing && existing.properties?.length
                  ? existing.properties
                  : nextRow.properties,
              hasLoadedProperties:
                existing.isEditing && existing.hasLoadedProperties
                  ? existing.hasLoadedProperties
                  : nextRow.hasLoadedProperties,
              accommodations:
                existing.isEditing && existing.accommodations?.length
                  ? existing.accommodations
                  : nextRow.accommodations,
              selectedAccommodationIds:
                existing.isEditing && existing.selectedAccommodationIds?.length
                  ? existing.selectedAccommodationIds
                  : nextRow.selectedAccommodationIds,
              hasLoadedAccommodations:
                existing.isEditing && existing.hasLoadedAccommodations
                  ? existing.hasLoadedAccommodations
                  : nextRow.hasLoadedAccommodations,
              initialSyncRangeMode:
                existing.initialSyncRangeMode || nextRow.initialSyncRangeMode,
              lookbackMonths: existing.lookbackMonths || nextRow.lookbackMonths,
            };
          },
        );
        if (draftRows.length) {
          this.hostkit.connectionRows.push(...draftRows);
        }
        this.hostkit.connectionRows.forEach((row) =>
          this.ensureSelectedPropertyOption(row),
        );
        this.hostkit.selectedConnectionId =
          data.active_connection_id || this.hostkit.connections[0]?.id || '';
        const activeConnection = this.hostkit.connections.find(
          (conn) => conn.id === this.hostkit.selectedConnectionId,
        );
        this.hostkit.mappingMode =
          activeConnection?.mapping_mode || 'all_accommodations';
        this.hostkit.unmappedAccommodationsCount =
          data.unmapped_accommodations_count || 0;
        this.hostkit.selectedPropertyId = data.hostkit_property_id || '';
        this.hostkit.selectedPropertyName = data.hostkit_property_name || '';
        if (data.is_connected) {
          this.hostkit.apiKeyMasked = '••••••••••••••••';
        }
        if (this.hostkit.selectedConnectionId) {
          const selectedRow = this.hostkit.connectionRows.find(
            (row) => row.id === this.hostkit.selectedConnectionId,
          );
          this.hostkit.properties = selectedRow?.properties || [];
        } else {
          this.hostkit.properties = [];
        }
      } catch (error) {
        console.error('Error loading Hostkit status:', error);
      } finally {
        this.hostkit.isLoadingConnections = false;
        this.hostkit.isStatusLoaded = true;
      }
    },

    async handleAutoInvoiceToggle() {
      // If turning ON, show confirmation
      if (this.invoicexpress.autoInvoiceEnabled) {
        const confirmed = confirm(
          'Enable Automatic Invoicing?\n\n' +
            '• Fully-paid bookings: Invoiced the day after checkout/service.\n' +
            '• Cancelled bookings with fees: Invoiced the day after cancellation.\n\n' +
            'SAFETY: Only applies to bookings that become invoiceable from today onwards.\n' +
            'Past bookings will NOT be invoiced automatically.\n\n' +
            'Do you want to enable automatic invoicing?',
        );
        if (!confirmed) {
          // User cancelled, revert the toggle
          this.invoicexpress.autoInvoiceEnabled = false;
          return;
        }
      }
      // Save the setting
      await this.saveInvoiceXpressSettings();
      // Reload to get the enabled_at date
      if (this.invoicexpress.autoInvoiceEnabled) {
        await this.loadInvoiceXpressSettings();
      }
    },

    async triggerAutoInvoice() {
      this.invoicexpress.isRunningAutoInvoice = true;
      try {
        const csrfToken = document.querySelector(
          '[name=csrfmiddlewaretoken]',
        ).value;
        const response = await fetch('/invoicexpress/auto-invoice/trigger/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
        });
        const data = await response.json();
        if (data.success) {
          const msg =
            data.invoices_issued > 0
              ? `Auto-invoice completed: ${data.invoices_issued} invoice(s) issued`
              : 'Auto-invoice completed: No eligible bookings found';
          Alpine.store('toastStore').addToast(msg, 'success');
        } else {
          Alpine.store('toastStore').addToast(
            data.error || 'Auto-invoice failed',
            'danger',
          );
        }
      } catch (error) {
        console.error('Error triggering auto-invoice:', error);
        Alpine.store('toastStore').addToast(
          'Error running auto-invoice',
          'danger',
        );
      } finally {
        this.invoicexpress.isRunningAutoInvoice = false;
      }
    },
  });

  return store;
}
